/**
 * Persistence Layer
 * Handles IndexedDB (offline cache), LocalStorage fallback, and Supabase cloud sync
 * Critical module for data integrity — all function bodies extracted verbatim from v0.7.1
 */

// --- IMPORTS ---
import { _sbReady, _sbUser, _isOnline, _sbHeaders, SB_REST, updateSyncBadge } from './auth.js';
import { S, SEED, SEED_R, SCALAR_FIELDS, mergeStates, schemaFloor, showConflictModal, runMigrations, save, seedScalarBaseline } from './state.js';
import { stripSecrets, preserveSecrets, hasSecrets } from './secrets.js';
import { uid, now } from './utils.js';
import { toast } from '../ui/layout.js';
import { CircuitBreaker } from './resilience.js';

// --- LOCAL STORAGE HELPER ---
let _lsQuotaWarned = false;
function safeLocalSave(data) {
  if (_localWritesBlocked('localStorage')) return;
  try {
    localStorage.setItem('zagx_v3', JSON.stringify(data));
    _lsQuotaWarned = false;
  } catch (e) {
    console.error('[ZAGX] localStorage quota exceeded:', e);
    if (!_lsQuotaWarned) {
      _lsQuotaWarned = true;
      toast('Storage full — data saved to IndexedDB only. Export a backup.', 'a-y');
    }
  }
}

// --- SUPABASE CIRCUIT BREAKER ---
// Prevents retry-storm when Supabase connection pool is exhausted (504s).
// After 3 consecutive failures → OPEN for 5 minutes (fail-fast, no requests).
const sbBreaker = new CircuitBreaker('Supabase', {
  failThreshold: 3,
  cooldownMs: 300000,    // 5 min — give connection pool time to recover
  successThreshold: 1
});
sbBreaker.onStateChange((state) => {
  if (state === 'OPEN') {
    console.warn('[ZAGX] Supabase circuit OPEN — pausing cloud sync for 5 min');
    updateSyncBadge('error', 'pool exhausted — retry in 5m');
    toast('Cloud sync paused (connection pool exhausted). Will retry in 5 min.', 'a-y');
    stopCloudPoll(); // stop polling while breaker is open
  } else if (state === 'CLOSED') {
    console.log('[ZAGX] Supabase circuit CLOSED — cloud sync resumed');
    updateSyncBadge('cloud', 'reconnected');
    toast('Cloud sync resumed', 'a-g');
    startCloudPoll(); // restart polling on recovery
  } else if (state === 'HALF_OPEN') {
    updateSyncBadge('cloud', 'probing...');
  }
});

/** Expose breaker for console diagnostics: window._sbBreaker in dev */
if (typeof window !== 'undefined') window._sbBreaker = sbBreaker;

/**
 * CPU-OPT: Simple string hash (djb2) — used to detect unchanged state payloads.
 * Avoids redundant Supabase upserts when save() fires but state hasn't actually changed.
 */
function _simpleHash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0x7fffffff;
  }
  return hash.toString(36);
}

// --- IDB CONSTANTS (re-exported for external use) ---
export const IDB_NAME = 'zagx_backups';
export const IDB_STORE = 'snapshots';

/**
 * The daily wealth snapshots the cloud copy carries.
 *
 * Snapshots were stripped from every outbound payload as "local-only timeline" data. The
 * consequence arrived on 2026-10-23: the owner's Chrome lost its local stores and the Total
 * Wealth chart drew three points, while the 10-17 backup held 126 days. A series that lives in
 * one browser profile is one cleared cache from gone, and a second device never sees it at all.
 * The merge has keyed snapshots by date since 2026-08-01; only the serialisers withheld them.
 *
 * Rows dated BEFORE today only. Today's row is rewritten on every Dashboard render as prices
 * move, and shipping it would change the payload hash on every save and defeat the unchanged-
 * hash skip. Yesterday's row is final; it goes up once. `today` is the LOCAL calendar day, the
 * same day `_dashSnapshot` stamps (it uses the UTC slice, which is one day behind local Istanbul
 * for three hours each night; either way a row is withheld for at most one extra day).
 */
export function syncableSnapshots(state, today) {
  const t = today || new Date().toISOString().slice(0, 10);
  return ((state && state.snapshots) || []).filter(s => s && typeof s.date === 'string' && s.date < t);
}
export const IDB_STATE = 'state';
export const IDB_MAX = 10;

// --- LOCAL STATE ---
let _idbReady = false;
let _idbConn = null;          // Persistent IDB connection (pooled — reused across operations)
let _onLoadComplete = null;
let _cloudVersion = 0;       // Optimistic locking: tracks server-side version counter
let _pollTimer = null;        // Periodic cloud polling timer
let _lastCloudSync = 0;       // Timestamp of last successful cloud load/save
// Set by load() when a read path ERRORED (as opposed to legitimately returning nothing).
// While true, cloudSave() refuses: an unverified local state must never be published.
let _loadFailed = false;
/** Did the last load() fail to establish what the account contains? (read-only) */
export function isLoadFailed() { return _loadFailed; }

// DATA-006. Narrower than `_loadFailed` on purpose — see shouldWriteLocalState.
let _localBlocked = false;
let _userInstalledState = false;
let _localBlockAnnounced = false;
// True from the first line of load() until the verdict is in. The reads are awaited — the cloud
// one behind two 10s timeouts — and `pagehide` fires on every app switch on iOS, so that window
// is long enough to lose a device to. It gates only the OUTSIDE writers (save(), the two unload
// handlers); load()'s own caching of what it just read has to go through. (DATA-006)
let _loadInProgress = false;

/**
 * May the LOCAL stores be written?
 *
 * `cloudSave` has refused to publish an unverified state since 2026-08-01. The local writers
 * never did, and on a boot where a read path threw, the empty in-memory state went over both
 * copies about a second later — `main.js` calls `refreshPrices()` right after `load()` and that
 * ends in a bare `save()`. Reproduced: IndexedDB holdings 0, localStorage holdings 0, cash.TRY 0.
 *
 * The predicate is deliberately NOT `_loadFailed`. That flag means "I could not establish what
 * this account holds", which is also true for a perfectly healthy first run while offline —
 * blocking there would stop a new user from ever saving, with nothing at stake. What we are
 * protecting is a copy that EXISTS ON DISK and could not be read:
 *
 *   localReadFailed && !loaded  →  there is something down there, we could not read it, and we
 *                                  have nothing from anywhere else to put in its place. BLOCK.
 *   localReadFailed && loaded   →  the cloud answered. Writing REPAIRS the unreadable copy.
 *   !localReadFailed            →  the disk was read and is genuinely empty. Nothing to lose.
 *
 * The escape hatch is not optional: importing or restoring a backup is exactly what someone does
 * after a failed load, so a deliberate install clears the block.
 *
 * Pure, so the rule is covered by a test rather than living inside load().
 */
export function shouldWriteLocalState({ localReadFailed, loaded, userInstalled }) {
  if (userInstalled) return true;
  return !(localReadFailed && !loaded);
}

/** The guard every local writer applies. ONE rule, N callers. */
function _localWritesBlocked(which) {
  if (!_localBlocked || _userInstalledState) return false;
  if (!_localBlockAnnounced) {
    _localBlockAnnounced = true;
    console.error('[ZAGX] local writes BLOCKED (' + which + ') — this device holds a copy that '
      + 'could not be read, and nothing else loaded. Refusing to write over it.');
    toast('There is saved data on this device that could not be read, so nothing has been '
      + 'written over it. Reload once you are online, or import your latest backup.',
    'a-r', { duration: 15000 });
  } else {
    console.warn('[ZAGX] local write skipped (' + which + ') — unreadable local copy protected');
  }
  return true;
}

/** For save() in state.js, which writes localStorage itself and cannot import this module. */
export function canWriteLocalState() {
  if (_userInstalledState) return true;
  return !_localBlocked && !_loadInProgress;
}

/**
 * The user deliberately installed a state — a file import, or a restore from the auto-backup
 * list. Clears the block so the recovery can reach the disk.
 */
export function markStateRestored() {
  _userInstalledState = true;
  _localBlockAnnounced = false;
  // The user just told us what the account holds, so the state is no longer a photograph of our
  // ignorance. Left standing, the mark would be written to disk with the recovery and make the
  // next boot refuse to trust it. (DATA-006 + the provisional marker)
  if (S._provisional) delete S._provisional;
  console.warn('[ZAGX] state installed by the user — local writes re-enabled');
}
let _lastCloudHash = '';      // Hash of last successfully saved state — skip duplicate writes

/**
 * Set callback for load() completion (used by render-loop to trigger renderAll)
 */
export function _setOnLoadComplete(fn) {
  _onLoadComplete = fn;
}

// --- IDB FUNCTIONS (IndexedDB offline cache) ---

/**
 * Open IndexedDB connection with version-aware migration
 * CE-04 Fix: Version-aware IDB migration (future-proofed for v3+)
 */
function idbOpen() {
  // Return cached connection if still alive
  if (_idbConn) {
    try {
      // Verify connection is usable by checking objectStoreNames
      _idbConn.objectStoreNames;
      return Promise.resolve(_idbConn);
    } catch (_e) {
      _idbConn = null; // Connection was closed/invalidated
    }
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 2);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      const oldV = e.oldVersion;
      if (oldV < 1) {
        if (!db.objectStoreNames.contains(IDB_STORE))
          db.createObjectStore(IDB_STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (oldV < 2) {
        if (!db.objectStoreNames.contains(IDB_STATE))
          db.createObjectStore(IDB_STATE);
      }
      // Future: if(oldV<3){ ... }
    };
    req.onsuccess = e => {
      _idbReady = true;
      _idbConn = e.target.result;
      // Clear cache if connection is closed externally
      _idbConn.onclose = () => { _idbConn = null; };
      _idbConn.onversionchange = () => { _idbConn.close(); _idbConn = null; };
      resolve(_idbConn);
    };
    req.onerror = e => reject(e.target.error);
  });
}

// ═══ MULTI-TAB SYNC (2026-07-24) ═══
// Two open tabs used to clobber each other: the stale tab's poll-triggered save()
// rewrote the whole IDB blob with pre-change rows (Option A `since` incident).
// Every IDB write now broadcasts; other tabs merge the fresh blob into their live S
// via the same mergeStates path the cloud poll uses (newest _updatedAt wins), so all
// tabs converge without user action. No cloud traffic. No loop: receivers only READ.
const _TAB_ID = Math.random().toString(36).slice(2, 10);
let _tabChannel = null;
let _tabSyncTimer = null;
let _tabSyncToastTs = 0; // price-poll saves broadcast constantly — merge every time, toast at most per 5 min
function _initTabSync() {
  if (_tabChannel !== null || typeof BroadcastChannel === 'undefined') return;
  try {
    _tabChannel = new BroadcastChannel('zagx_tab_sync');
    _tabChannel.onmessage = ev => {
      if (!ev.data || ev.data.tab === _TAB_ID) return;
      clearTimeout(_tabSyncTimer); // coalesce bursts (price-poll saves fire in clusters)
      _tabSyncTimer = setTimeout(async () => {
        try {
          const fresh = await idbLoadState();
          if (!fresh) return;
          const { state: merged } = mergeStates(S, fresh);
          Object.assign(S, merged);
          seedScalarBaseline(); // next save must not misattribute merged fields (mirrors D-11)
          if (Date.now() - _tabSyncToastTs > 300000) { toast('Synced changes from another tab', 'a-g'); _tabSyncToastTs = Date.now(); }
          if (_onLoadComplete) _onLoadComplete();
        } catch (e) { console.warn('[TABSYNC] merge failed:', e.message); }
      }, 400);
    };
  } catch (_e) { _tabChannel = false; /* unavailable — single-tab behavior unchanged */ }
}
function _tabSyncBroadcast() {
  _initTabSync();
  try { if (_tabChannel) _tabChannel.postMessage({ tab: _TAB_ID, ts: Date.now() }); } catch (_e) { /* best-effort */ }
}
_initTabSync(); // listener from boot — a purely-viewing tab must receive even if it never writes

/**
 * Save full state to IDB 'state' store (replaces main entry)
 */
export async function idbSaveState(data) {
  if (_localWritesBlocked('idb-state')) return false;
  try {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STATE, 'readwrite');
      const st = tx.objectStore(IDB_STATE);
      st.put(JSON.stringify(data), 'main');
      tx.oncomplete = () => { resolve(true); _tabSyncBroadcast(); };
      tx.onerror = e => reject(e.target.error);
    });
  } catch(e) {
    console.warn('[IDB] state save failed:', e);
    return false;
  }
}

/**
 * Load full state from IDB 'state' store
 */
export async function idbLoadStateResult() {
  try {
    const db = await idbOpen();
    return await new Promise(resolve => {
      const tx = db.transaction(IDB_STATE, 'readonly');
      const st = tx.objectStore(IDB_STATE);
      const req = st.get('main');
      req.onsuccess = () => {
        if (!req.result) return resolve({ ok: true, state: null, why: 'empty' });
        try {
          resolve({ ok: true, state: JSON.parse(req.result) });
        } catch(_pe) {
          // A stored blob that will not parse is data that EXISTS and cannot be read.
          resolve({ ok: false, state: null, why: 'unparseable' });
        }
      };
      req.onerror = e => resolve({ ok: false, state: null, why: 'request-error:' + (e && e.target && e.target.error) });
    });
  } catch(e) {
    return { ok: false, state: null, why: 'open-threw:' + e };
  }
}

/**
 * Back-compat shape for callers that only want the state. DERIVED from the result above —
 * not a second implementation, because two copies of this rule is how it got lost the
 * first time.
 */
export async function idbLoadState() {
  const r = await idbLoadStateResult();
  return r.ok ? r.state : null;
}

/**
 * Clear full state from IDB 'state' store
 */
export async function idbClearState() {
  try {
    const db = await idbOpen();
    const tx = db.transaction(IDB_STATE, 'readwrite');
    tx.objectStore(IDB_STATE).delete('main');
  } catch(_e) {}
}

/**
 * Tax Module snapshots (VIEWER architecture) — separate key in the 'state' store.
 * LOCAL ONLY by design: never merged into S, never part of save()/cloudSave(),
 * so tax data can never reach the Supabase blob. No tab-sync broadcast (the
 * broadcast path merges the 'main' state entry; this key is invisible to it).
 */
export async function idbSaveTax(data) {
  try {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STATE, 'readwrite');
      tx.objectStore(IDB_STATE).put(JSON.stringify(data), 'tax_snapshots');
      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject(e.target.error);
    });
  } catch(e) {
    console.warn('[IDB] tax save failed:', e);
    return false;
  }
}

export async function idbLoadTax() {
  try {
    const db = await idbOpen();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STATE, 'readonly');
      const req = tx.objectStore(IDB_STATE).get('tax_snapshots');
      req.onsuccess = () => {
        if (!req.result) return resolve(null);
        try { resolve(JSON.parse(req.result)); } catch(_pe) { resolve(null); }
      };
      req.onerror = () => resolve(null);
    });
  } catch(_e) {
    return null;
  }
}

/**
 * Statement snapshots (Income & Expenses > Statements), same contract as the tax key above:
 * a separate key in the 'state' store, LOCAL ONLY, never merged into S, never part of
 * save()/cloudSave(), invisible to the tab-sync broadcast. The engine on the owner's machine
 * writes the cloud table; the app only ever reads it and caches it here.
 */
export async function idbSaveStatements(data) {
  try {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STATE, 'readwrite');
      tx.objectStore(IDB_STATE).put(JSON.stringify(data), 'statement_snapshots');
      tx.oncomplete = () => resolve(true);
      tx.onerror = e => reject(e.target.error);
    });
  } catch(e) {
    console.warn('[IDB] statements save failed:', e);
    return false;
  }
}

export async function idbLoadStatements() {
  try {
    const db = await idbOpen();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STATE, 'readonly');
      const req = tx.objectStore(IDB_STATE).get('statement_snapshots');
      req.onsuccess = () => {
        if (!req.result) return resolve(null);
        try { resolve(JSON.parse(req.result)); } catch(_pe) { resolve(null); }
      };
      req.onerror = () => resolve(null);
    });
  } catch(_e) {
    return null;
  }
}

/**
 * Save snapshot to IDB 'snapshots' store (keeps history, auto-prunes to IDB_MAX)
 */
export async function idbSave(data) {
  if (_localWritesBlocked('idb-snapshot')) return false;
  try {
    const db = await idbOpen();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const st = tx.objectStore(IDB_STORE);
    st.add({ ts: Date.now(), data: JSON.stringify(data) });
    const countReq = st.count();
    countReq.onsuccess = () => {
      if (countReq.result > IDB_MAX) {
        const cur = st.openCursor();
        let toDelete = countReq.result - IDB_MAX;
        cur.onsuccess = e => {
          const c = e.target.result;
          if (c && toDelete > 0) {
            c.delete();
            toDelete--;
            c.continue();
          }
        };
      }
    };
  } catch(_e) {}
}

/**
 * List all snapshots from IDB (newest first)
 */
export async function idbList() {
  try {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const st = tx.objectStore(IDB_STORE);
      const req = st.getAll();
      req.onsuccess = () => resolve(req.result.reverse());
      req.onerror = e => reject(e.target.error);
    });
  } catch(_e) {
    return [];
  }
}

/**
 * Restore specific snapshot by ID
 */
export async function idbRestore(id) {
  try {
    const db = await idbOpen();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const st = tx.objectStore(IDB_STORE);
      const req = st.get(id);
      req.onsuccess = () => {
        if (req.result) resolve(JSON.parse(req.result.data));
        else reject('Not found');
      };
      req.onerror = e => reject(e.target.error);
    });
  } catch(_e) {
    return null;
  }
}

/**
 * Clear all snapshots from IDB
 */
export async function idbClear() {
  try {
    const db = await idbOpen();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).clear();
  } catch(_e) {}
}

// --- SUPABASE CLOUD OPERATIONS (direct fetch — bypasses SDK REST bugs) ---

/**
 * Save current state to Supabase (optimistic locking with version counter)
 *
 * Multi-device safety:
 *   1. Client sends _cloudVersion in state JSON
 *   2. Supabase trigger checks if client version matches DB version
 *   3. On match: DB auto-increments version, save succeeds
 *   4. On mismatch (409): another device saved first → re-fetch, merge, retry
 *
 * CE-01 Fix: Atomic upsert — eliminates check-then-write race condition
 * SYNC-01 Fix: Version-based optimistic locking — prevents silent overwrites
 */
let _cloudSaveInFlight = false;
let _cloudSavePending = false;

export async function cloudSave() {
  if (!_sbReady || !_sbUser || !_isOnline) return false;

  // DATA-LOSS GUARD (audit 2026-08-01). If load() could not establish what the account
  // actually contains, the in-memory state is not a state to publish — it is the absence
  // of one. Writing it upserts over the real user_state row while the badge goes green.
  if (_loadFailed) {
    console.error('[ZAGX] cloudSave BLOCKED — the last load failed, refusing to overwrite the cloud with unverified local state. Reload once you are online.');
    updateSyncBadge('error', 'load failed — not syncing');
    return false;
  }

  // STABILIZATION: Mutex — only 1 cloudSave in-flight at a time
  // If one is already running, mark pending (will re-run after current completes)
  if (_cloudSaveInFlight) {
    _cloudSavePending = true;
    console.log('[ZAGX] cloudSave skipped — already in-flight, queued pending');
    return false;
  }
  _cloudSaveInFlight = true;
  _cloudSavePending = false;

  try {
    return await _cloudSaveInner();
  } finally {
    _cloudSaveInFlight = false;
    // If a save was requested while we were busy, run one more time
    if (_cloudSavePending) {
      _cloudSavePending = false;
      console.log('[ZAGX] Running pending cloudSave');
      setTimeout(() => cloudSave(), 1000); // 1s delay to let DB breathe
    }
  }
}

async function _cloudSaveInner() {
  // Circuit breaker: fail-fast if Supabase is down
  if (!sbBreaker.canRequest()) {
    console.log('[ZAGX] cloudSave blocked — Supabase circuit OPEN');
    return false;
  }

  const MAX_RETRIES = 3;
  const BASE_DELAY = 1000;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const headers = await _sbHeaders();
      if (!headers) {
        console.warn('[SB] No active session for cloud save');
        updateSyncBadge('error', 'no session');
        return false;
      }
      updateSyncBadge('cloud', attempt > 1 ? `retry ${attempt}/${MAX_RETRIES}...` : 'saving...');

      // STABILIZATION: Strip transient/re-fetchable data before cloud save
      // prices: re-fetched on every page load
      // history: audit log kept locally, not needed for cross-device sync (47KB+)
      // snapshots: local-only timeline snapshots
      // _fxTs, _saveCount: session-only tracking
      // alertedKeys: session-specific alert dedup
      let { prices: _prices, history: _history, snapshots: _snapshots, _fxTs, _saveCount, alertedKeys: _alertedKeys, ...persistentState } = S;
      persistentState.snapshots = syncableSnapshots(S);
      // Strip render-derived drift cache (recomputed each render; lastDriftTs churns the payload
      // every frame and defeats the unchanged-hash skip). audit M-8.
      if (persistentState.rebalancing && (persistentState.rebalancing.lastDrift || persistentState.rebalancing.lastDriftTs)) {
        const { lastDrift: _ld, lastDriftTs: _lts, ...rb } = persistentState.rebalancing;
        persistentState.rebalancing = rb;
      }
      // Keep user API keys device-local — never sync secrets to the cloud. audit M-10.
      // Was a hand-rolled destructure here and a different one in cloudSnapshot; the third
      // serialiser (doBackup) had neither, which is how two live keys reached a git history.
      // core/secrets.js now owns the list. (audit 2026-08-03)
      persistentState = stripSecrets(persistentState).state;
      // `hasSecrets` was written to "assert a payload before it is sent" and had ZERO callers —
      // a guard that exists and is never asked (rule #27). Asked now, at both serialisers. If
      // this ever fires, a new secret path reached the state without being added to
      // SECRET_PATHS, and the right answer is to refuse the write rather than publish it.
      if (hasSecrets(persistentState)) {
        console.error('[SB] refusing to upload: payload still carries a secret path');
        return false;
      }
      persistentState._cloudVersion = _cloudVersion;

      const upsertHeaders = { ...headers, 'Prefer': 'resolution=merge-duplicates,return=representation' };
      const body = JSON.stringify({ user_id: _sbUser.id, state: persistentState });

      // CPU-OPT: Skip cloud write if state hasn't changed since last successful save
      // Uses simple string hash to detect duplicate payloads — eliminates ~40% of redundant upserts
      const payloadHash = _simpleHash(body);
      if (payloadHash === _lastCloudHash && attempt === 1) {
        console.log('[ZAGX] cloudSave skipped — state unchanged (hash match)');
        updateSyncBadge('cloud', 'synced');
        return true;
      }

      console.log('[ZAGX] Cloud payload:', Math.round(body.length / 1024) + 'KB (stripped prices/transient)');
      // STABILIZATION: 15s timeout to prevent hanging connections
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      let resp;
      try {
        resp = await fetch(SB_REST + '/user_state?on_conflict=user_id', {
          method: 'POST',
          headers: upsertHeaders,
          body,
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeout);
      }

      if (resp.ok) {
        // Extract new version from response
        const rows = await resp.json();
        if (Array.isArray(rows) && rows.length > 0 && typeof rows[0].version === 'number') {
          _cloudVersion = rows[0].version;
        } else {
          _cloudVersion++; // Fallback: assume incremented
        }
        _lastCloudSync = Date.now();
        _lastCloudHash = payloadHash; // CPU-OPT: remember hash to skip duplicate writes
        sbBreaker.recordSuccess();
        updateSyncBadge('cloud', 'synced');
        console.log('[ZAGX] Cloud save OK (v' + _cloudVersion + ')');
        return true;
      }

      const errBody = await resp.text();

      // VERSION CONFLICT (409 or Supabase 400 with VERSION_CONFLICT)
      if (resp.status === 409 || (resp.status === 400 && errBody.includes('VERSION_CONFLICT'))) {
        console.warn('[ZAGX] Version conflict detected — merging with cloud (attempt ' + attempt + ')');
        updateSyncBadge('cloud', 'merging...');

        // Re-fetch cloud state
        const cloudData = await cloudLoad();
        if (cloudData) {
          // Merge local ↔ cloud
          const { state: merged, conflicts, hasConflicts } = mergeStates(S, cloudData);
          Object.assign(S, merged);
          // `seedScalarBaseline` says: call after load() and after ANY poll/conflict merge, or
          // the next save() marks every scalar changed-now and stomps a newer cloud scalar from
          // another device. Three of the four merge sites did (load, poll, tab-sync); this one
          // did not. Concretely: A takes B's `budgets` in this merge, `_prevScalars.budgets`
          // still holds A's pre-merge value, so A's next save — for any unrelated reason —
          // stamps `budgets` as its own write and beats a third device's genuine older edit.
          // (audit 2026-08-08)
          seedScalarBaseline();

          // Update local caches
          idbSaveState(S);
          safeLocalSave(S);

          if (hasConflicts) {
            console.log('[ZAGX] Merge after conflict: ' + conflicts.length + ' conflicts');
            setTimeout(() => showConflictModal(conflicts, merged), 500);
          } else {
            console.log('[ZAGX] Clean merge after version conflict ✓');
          }
          lg('sync', '—', 'Version conflict resolved (v' + _cloudVersion + ')');

          // Retry with updated version (next loop iteration)
          continue;
        }
        // If cloudLoad failed, fall through to retry
      }

      console.error('[SB] Cloud save error (attempt ' + attempt + '):', resp.status, errBody);

      // Don't retry on auth errors (401/403)
      if (resp.status === 401 || resp.status === 403) {
        updateSyncBadge('error', 'auth error');
        return false;
      }

      // Retry on server/network errors (5xx, 429)
      if (attempt < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);
        await new Promise(r => setTimeout(r, delay));
      }
    } catch(e) {
      console.error('[SB] Cloud save exception (attempt ' + attempt + '):', e);
      if (attempt < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, attempt - 1);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }

  sbBreaker.recordFailure();
  // D-12: clear the dedupe hash so the NEXT save actually writes — otherwise a state that only ever
  // hit transient failures could be hash-skipped forever (LS changed, cloud never persisted).
  _lastCloudHash = '';
  updateSyncBadge('error', 'sync failed');
  console.error('[SB] Cloud save failed after ' + MAX_RETRIES + ' attempts');
  return false;
}

/**
 * Load state from Supabase (also captures version for optimistic locking)
 * Graceful fallback: if `version` column doesn't exist (migration not run), retry without it.
 */
export async function cloudLoadResult() {
  // Not asked, so not answered. `ok:false` and the caller must not score this as "consulted".
  if (!_sbReady || !_sbUser || !_isOnline) return { ok: false, state: null, why: 'not-configured-or-offline' };

  // Circuit breaker: fail-fast if Supabase is down
  if (!sbBreaker.canRequest()) {
    console.log('[ZAGX] cloudLoad blocked — Supabase circuit OPEN');
    return { ok: false, state: null, why: 'circuit-open' };
  }

  try {
    const headers = await _sbHeaders();
    if (!headers) return { ok: false, state: null, why: 'no-auth-headers' };
    headers['Prefer'] = 'return=representation';

    // STABILIZATION: 10s timeout for cloudLoad
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    let resp;
    try {
      // Try with version column first
      resp = await fetch(SB_REST + '/user_state?user_id=eq.' + _sbUser.id + '&select=state,updated_at,version', { headers, signal: controller.signal });

      // If 400 (likely missing `version` column), retry without it
      if (resp.status === 400) {
        console.warn('[SB] cloudLoad: version column not found — falling back without it');
        const controller2 = new AbortController();
        const timeout2 = setTimeout(() => controller2.abort(), 10000);
        try {
          resp = await fetch(SB_REST + '/user_state?user_id=eq.' + _sbUser.id + '&select=state,updated_at', { headers, signal: controller2.signal });
        } finally {
          clearTimeout(timeout2);
        }
      }
    } finally {
      clearTimeout(timeout);
    }

    if (!resp.ok) {
      console.error('[SB] Cloud load error:', resp.status);
      sbBreaker.recordFailure();
      // A 5xx says NOTHING about what the account holds. Returning a bare null here is what
      // let one Supabase outage score as "proven empty" and seed 29 rows over a real
      // portfolio. (DATA-007, audit 2026-08-13)
      return { ok: false, state: null, why: 'http-' + resp.status };
    }
    const rows = await resp.json();
    if (!Array.isArray(rows) || rows.length === 0) {
      sbBreaker.recordSuccess(); // request worked, just no data
      return { ok: true, state: null, why: 'no-row' }; // First time user
    }
    const data = rows[0];
    if (data && data.state && typeof data.state === 'object' && Array.isArray(data.state.holdings)) {
      // Capture version for optimistic locking
      if (typeof data.version === 'number') {
        _cloudVersion = data.version;
        console.log('[ZAGX] Cloud load OK (v' + _cloudVersion + ', updated:', data.updated_at, ')');
      } else {
        console.log('[ZAGX] Cloud load OK (no version col, updated:', data.updated_at, ')');
      }
      _lastCloudSync = Date.now();
      sbBreaker.recordSuccess();
      return { ok: true, state: data.state };
    }
    sbBreaker.recordSuccess();
    // The row exists but its shape is not a state. The request ANSWERED, so this is a real
    // "nothing usable there", not a failure to find out.
    return { ok: true, state: null, why: 'row-not-a-state' };
  } catch(e) {
    console.error('[SB] Cloud load exception:', e);
    sbBreaker.recordFailure();
    return { ok: false, state: null, why: 'threw:' + e };
  }
}

/**
 * Back-compat shape for callers that only want the state (the 5-minute poll, main.js).
 * DERIVED from cloudLoadResult — one implementation, one rule.
 */
export async function cloudLoad() {
  const r = await cloudLoadResult();
  return r.ok ? r.state : null;
}

/**
 * Create snapshot on Supabase (audit trail)
 */
export async function cloudSnapshot(note) {
  if (!_sbReady || !_sbUser || !_isOnline) return;
  if (!sbBreaker.canRequest()) return; // fail-fast when pool exhausted
  try {
    const headers = await _sbHeaders();
    if (!headers) return;
    // PERF-01: Strip transient/prices data like cloudSave does
    let stripped = JSON.parse(JSON.stringify(S));
    delete stripped.prices;
    delete stripped._transient;
    delete stripped._renderState;
    if (stripped.rebalancing) { delete stripped.rebalancing.lastDrift; delete stripped.rebalancing.lastDriftTs; }
    stripped = stripSecrets(stripped).state;
    if (hasSecrets(stripped)) {
      console.error('[SB] refusing to snapshot: payload still carries a secret path');
      return;
    }
    await fetch(SB_REST + '/state_snapshots', {
      method: 'POST', headers,
      body: JSON.stringify({ user_id: _sbUser.id, state: stripped, note: note || 'auto' })
    });
  } catch(e) {
    console.warn('[SB] Snapshot failed:', e);
  }
}

/**
 * May an empty in-memory state be seeded (and therefore published to the cloud)?
 *
 * Only when the emptiness is PROVEN: every read path completed without error, and at
 * least one of them actually reported on the account. A read that blew up tells us
 * nothing about what the account contains, and treating "we don't know" as "it's empty"
 * is how 29 zero-qty seed rows end up upserted over a real portfolio with the sync badge
 * showing green. Being wrong in the cautious direction costs one reload; being wrong in
 * the other direction costs the portfolio. (audit 2026-08-01)
 *
 * Pure and exported so the decision is covered by a test instead of living inside load().
 */
export function shouldSeedEmptyState({ localReadFailed, cloudReadFailed, cloudConsulted, loaded, loadedProvisional }) {
  if (localReadFailed || cloudReadFailed) return false;
  // A blob this app wrote DURING a failed load is not evidence about the account — it is a
  // photograph of our own ignorance, and reading it back as fact is how a one-off outage
  // becomes permanent. `save()` keeps running after a refused load (a price refresh alone
  // triggers it), so the empty in-memory state reaches the disk, and the NEXT boot loads it as
  // a perfectly valid empty account, scores it proven-empty, seeds 29 rows and publishes them.
  // The outage lasted a minute; the seeding is forever. (DATA-006, audit 2026-08-13)
  if (loadedProvisional) return !!cloudConsulted;
  return !!loaded || !!cloudConsulted;
}

// --- MAIN LOAD FUNCTION ---

/**
 * Load state from IDB/localStorage/cloud, merge if needed, run migrations, init SEED
 * Called at app startup (after auth is ready)
 */
export async function load() {
  let _loaded = false;
  let localData = null;
  let cloudData = null;
  // Distinguish "read returned nothing" from "read blew up". `!S.holdings.length` cannot,
  // and that is what let a failed boot re-seed and publish an empty portfolio. (audit 2026-08-01)
  let localReadFailed = false;
  let cloudReadFailed = false;
  let cloudConsulted = false;
  // Was the copy we ended up using written by a boot that had already given up? See
  // `shouldSeedEmptyState` and the `_provisional` stamp below. (DATA-006)
  let loadedProvisional = false;
  _loadInProgress = true;
  _localBlocked = false;
  _localBlockAnnounced = false;
  // NOTE: `_userInstalledState` is deliberately NOT cleared here. Clearing it at the top would
  // revoke a completed import's permission for the whole read window, which is the one moment
  // its owner is most likely to be doing something. It is cleared with the verdict below.
  _loadFailed = false;

  // Step 1: Load local data (IDB first, then LS fallback).
  //
  // The try/catch here used to be the only thing that could set `localReadFailed`, and
  // `idbLoadState` swallowed every error and returned null — so the flag was unreachable and
  // an IndexedDB failure scored as "there is nothing there". The read now reports its own
  // outcome. (DATA-007, audit 2026-08-13)
  try {
    const idbRes = await idbLoadStateResult();
    if (!idbRes.ok) {
      console.warn('[ZAGX] IDB load failed:', idbRes.why);
      localReadFailed = true;
    } else if (idbRes.state && typeof idbRes.state === 'object'
      && typeof idbRes.state._schemaVersion === 'number' && Array.isArray(idbRes.state.holdings)) {
      localData = idbRes.state;
      loadedProvisional = !!idbRes.state._provisional;
      console.log('[ZAGX] Local state from IndexedDB ✓'
        + (loadedProvisional ? ' (marked PROVISIONAL — written during a failed load)' : ''));
    } else if (idbRes.state) {
      // Read fine, cannot USE it: no numeric `_schemaVersion`, or `holdings` is not an array.
      // That is data that EXISTS and we failed to make sense of — the same answer as a throw,
      // and the distinction the rest of this file is built on. Falling through to `localData =
      // null` scored it as "nothing there" and the next save() wrote the empty state over a
      // full portfolio. Reproduced 2026-08-13. It also silently discarded a hand-pasted
      // recovery blob, which is one of the routes the block's own toast recommends.
      console.warn('[ZAGX] IDB state rejected — present but not a usable state');
      localReadFailed = true;
    }
  } catch(e) {
    console.warn('[ZAGX] IDB load threw:', e);
    localReadFailed = true;
  }
  if (!localData) {
    // getItem itself throws where storage is blocked. Unhandled, that rejected load() with the
    // flag still false and left an empty S behind a permissive guard.
    let raw = null;
    try {
      raw = localStorage.getItem('zagx_v3');
    } catch(e) {
      console.warn('[ZAGX] LocalStorage is not readable:', e);
      localReadFailed = true;
    }
    if (raw) try {
      const lsState = JSON.parse(raw);
      // The IDB branch has always demanded a usable shape and this one never did, so the two
      // readers disagreed about the same blob: dropped by one, adopted by the other.
      if (lsState && typeof lsState === 'object'
        && typeof lsState._schemaVersion === 'number' && Array.isArray(lsState.holdings)) {
        localData = lsState;
        loadedProvisional = !!localData._provisional;
        console.log('[ZAGX] Local state from LocalStorage ✓'
          + (loadedProvisional ? ' (marked PROVISIONAL — written during a failed load)' : ''));
      } else {
        console.warn('[ZAGX] LocalStorage state rejected — present but not a usable state');
        localReadFailed = true;
      }
    } catch(_e) {
      // A present-but-unparseable blob means data EXISTS and we cannot read it.
      console.warn('[ZAGX] LocalStorage state is present but unparseable');
      localReadFailed = true;
    }
  }

  // Step 2: Load cloud data (if online + authenticated).
  //
  // `cloudConsulted` used to be set BEFORE the request, so a 5xx scored as "we asked and the
  // account is empty" — and `shouldSeedEmptyState` then authorised 29 seed rows over the real
  // user_state row. It is set from the ANSWER now, never from the intention. (DATA-007)
  try {
    const cloudRes = await cloudLoadResult();
    cloudConsulted = cloudRes.ok;
    if (!cloudRes.ok) {
      // "Offline" and "not signed in" are not failures to read — there was nothing to read
      // from. Only a request that was made and did not answer counts against the seed.
      const notAsked = cloudRes.why === 'not-configured-or-offline';
      if (!notAsked) {
        console.warn('[ZAGX] Cloud load failed:', cloudRes.why);
        cloudReadFailed = true;
      }
    } else if (cloudRes.state && typeof cloudRes.state === 'object' && Array.isArray(cloudRes.state.holdings)
      && typeof cloudRes.state._schemaVersion === 'number') {
      cloudData = cloudRes.state;
      // A cloud copy that answered is evidence even if the local one was provisional.
      if (!cloudData._provisional) loadedProvisional = false;
      console.log('[ZAGX] Cloud state loaded ✓');
    } else if (cloudRes.state) {
      // Answered, but what came back is not a state we can use. The IDB and localStorage
      // branches both demand a numeric `_schemaVersion` — a payload without one does not
      // overwrite `S._schemaVersion` on Object.assign, so `runMigrations` reads the fresh-state
      // default as `fromV` and skips every migration beneath it. This was the third reader and
      // the last one still missing that check. (DATA-005)
      console.warn('[ZAGX] Cloud state rejected — answered, but not a usable state');
      cloudReadFailed = true;
    }
  } catch(e) {
    console.warn('[ZAGX] Cloud load threw:', e);
    cloudData = null;
    cloudReadFailed = true;
  }

  // Step 3: Merge strategy
  if (cloudData && localData) {
    // Both exist — field-level merge
    const { state: merged, conflicts, hasConflicts } = mergeStates(localData, cloudData);
    Object.assign(S, merged);
    _loaded = true;
    if (hasConflicts) {
      console.log('[ZAGX] Merge conflicts detected:', conflicts.length);
      // Show modal after render
      setTimeout(() => showConflictModal(conflicts, merged), 500);
      updateSyncBadge('cloud', 'conflicts!');
    } else {
      console.log('[ZAGX] Clean merge — no conflicts ✓');
      updateSyncBadge('cloud', 'merged');
    }
    // Update both local caches with merged state
    idbSaveState(S);
    safeLocalSave(S);
    // Push merged state to cloud
    cloudSave();
    // Log only CONFLICTED merges — clean merges are routine and were drowning the audit
    // buffer (2026-07-15 finding: 823/1000 entries were "Merge: clean"; zero trade/cash/price
    // events survived). Conflicts, version conflicts and remote-update polls still log.
    if (hasConflicts) lg('sync', '—', 'Merge: ' + conflicts.length + ' conflicts');
  } else if (cloudData) {
    // Cloud only (fresh device)
    // SEC-001's second door: this path never goes through mergeStates, so it needs the same
    // preserve. A key typed into THIS session before the first cloud read would otherwise be
    // erased by a row that, correctly, never carried one.
    preserveSecrets(S, cloudData);
    Object.assign(S, cloudData);
    _loaded = true;
    console.log('[ZAGX] State loaded from Cloud (no local data) ✓');
    idbSaveState(S);
    safeLocalSave(S);
    updateSyncBadge('cloud', 'synced');
  } else if (localData) {
    // Local only (offline, first cloud sync — or a cloud read that FAILED).
    Object.assign(S, localData);
    _loaded = true;
    console.log('[ZAGX] State loaded from local (offline) ✓');
    updateSyncBadge('offline', 'local cache');
    // Publishing here means "local is the truth". That is fair when there was nothing in the
    // cloud, and false when the cloud could not be read: a 5xx on the state read used to send
    // the local copy — `holdings: []` for a cash-only user — straight over a row nobody had
    // looked at. The verdict block below would then declare the load failed, after the write.
    // (DATA-007 round 2, audit 2026-08-13)
    if (_sbReady && _isOnline && !cloudReadFailed) cloudSave();
    else if (cloudReadFailed) console.warn('[ZAGX] not publishing local over a cloud row that could not be read');
  }

  // DATA-006: decide now, after the reads and after Step 3 has had its chance to repair an
  // unreadable local copy from the cloud, and before anything else can write.
  _localBlocked = !shouldWriteLocalState({ localReadFailed, loaded: _loaded, userInstalled: false });
  _userInstalledState = false;
  _loadInProgress = false;
  if (_localBlocked) console.warn('[ZAGX] local writes blocked for this session — unreadable local copy, nothing loaded');

  // SCALAR RESCUE (audit 2026-08-01). Whichever path ran above, a scalar the LOCAL
  // snapshot holds must not be silently absent from the result. The cloud-only branch is
  // a plain Object.assign, so it cannot restore a field the cloud has never received —
  // and a 15s debounce means the cloud routinely has not received the newest write yet.
  // Restore only when local actually has data and the result does not: an intentional
  // clear elsewhere still wins, because it would leave data on neither side.
  if (localData) {
    SCALAR_FIELDS.forEach(key => {
      if (S[key] === undefined && localData[key] !== undefined) {
        S[key] = localData[key];
        console.warn('[ZAGX] restored scalar "' + key + '" from the local snapshot — the loaded copy did not carry it');
      }
    });
  }

  // SCHEMA FLOOR (audit 2026-08-03). Whichever branch above ran, the version that reaches
  // runMigrations must be the highest any store has seen. The merged path now keeps the max
  // itself, but the cloud-only and local-only branches are plain Object.assign and will
  // happily install an older number from a copy that has not caught up. Live consequence:
  // a load arrived at v34 and re-ran v35..v40; two of those were written as deltas rather
  // than targets, and five positions silently took TRY 63,485 of assets that were never
  // bought.
  //
  // ⚠ "MIGRATIONS ARE ABSOLUTE NOW, SO A RE-RUN IS SURVIVABLE" USED TO STAND HERE AND IT IS
  // WRONG. Measured 2026-08-16 on the live book: absolute makes a migration idempotent with
  // respect to ITSELF, not safe against the world moving on. Replaying v40 today writes back two
  // positions he has since closed and rolls a cash holding down. The re-run is not survivable;
  // this floor is the reason it never happens. (rule #45)
  //
  // One definition, in `state.schemaFloor` — `mergeStates` is the other caller.
  {
    const seen = schemaFloor(
      S._schemaVersion,
      localData && localData._schemaVersion,
      cloudData && cloudData._schemaVersion,
    );
    if (seen > (Number(S._schemaVersion) || 0)) {
      console.warn('[ZAGX] loaded state says schema v' + S._schemaVersion + ' but v' + seen +
        ' was already reached — holding at v' + seen + ' rather than replaying migrations');
      S._schemaVersion = seen;
    }
  }
  // THE VERDICT COMES BEFORE THE POST-MIGRATION WRITE (audit 2026-08-13).
  //
  // This block used to sit ~90 lines further down, AFTER the persist below. A fresh S starts at
  // the schema default and runMigrations advances it, so on every boot where nothing loaded
  // that persist fired — writing the EMPTY state to both local stores and publishing it —
  // while `_loadFailed` was still false and every guard, including cloudSave's own, read
  // permission. Reproduced: `isLoadFailed()` true and `zagx_v3` holding
  // `{"_schemaVersion":48,...,"holdings":[]}` after the same load.
  //
  // NOT "before any write", and the difference matters. Step 3 above still writes the local
  // caches on every branch that loaded something, which is correct — that is the state it just
  // read. Its cloud publish is the one that had to be gated, and it is, on `cloudReadFailed`.
  // What remains unguarded is `save()` itself (state.js), which writes both local stores with
  // no reference to `_loadFailed`: one `refreshPrices()` after a failed boot still lays the
  // empty state down, and the NEXT boot reads it back as a legitimate empty account. That is
  // the next commit, not this one. (DATA-006)
  //
  // On the move itself: exactly one migration adds a holding — v16 (`state.js:1116`, the SAP
  // row) — and it cannot run here, because a fresh S starts at CURRENT_SCHEMA and 16 < 29. So
  // the emptiness test is as valid before runMigrations as it was after it.
  const _provablyEmpty = shouldSeedEmptyState({ localReadFailed, cloudReadFailed, cloudConsulted, loaded: _loaded, loadedProvisional });
  if (!S.holdings.length && !_provablyEmpty) {
    _loadFailed = true;
    // Stamp the state itself, not a module variable. Every writer serialises S — the two in
    // this file, the one inside save(), and the two unload handlers in main.js — so one field
    // reaches all five without any of them having to know the rule. The user keeps working and
    // keeps their session; what they do not get is a blob that lies to the next boot.
    S._provisional = true;
    console.error('[ZAGX] Empty state after an unverified load — refusing to seed.',
      { localReadFailed, cloudReadFailed, cloudConsulted, loaded: _loaded });
    updateSyncBadge('error', 'load failed — not syncing');
    // Do not promise the cloud. An offline or signed-out user has no cloud copy, and telling
    // him his data is safe there is the one sentence he must not be given if it is not true.
    // `toast(msg, cls, 12000)` reads as 12 seconds and is not: the third parameter is an
    // options object (`ui/layout.js:35` uses `opts.duration`), so a bare number silently takes
    // the 5s default. On the one message that has to survive being glanced at, it mattered.
    toast('Could not verify your saved data, so nothing has been written. Reload once you are '
      + 'online — and if that does not bring it back, import your latest backup.', 'a-r', { duration: 15000 });
  }

  const preV = S._schemaVersion;
  runMigrations();
  // If migrations changed the schema version, persist immediately to cloud — but never on a
  // load that could not establish what the account holds.
  if (S._schemaVersion > preV && !_loadFailed) {
    console.log('[ZAGX] Migrations advanced schema ' + preV + ' → ' + S._schemaVersion + ' — forcing cloud save');
    idbSaveState(S);
    safeLocalSave(S);
    if (_sbReady && _isOnline) cloudSave();
  }
  // Defensive guard: ensure S.cash always exists post-migration
  if (!S.cash || typeof S.cash !== 'object') S.cash = { USD: 0, TRY: 0, EUR: 0 };
  ['USD', 'TRY', 'EUR'].forEach(c => {
    if (typeof S.cash[c] !== 'number' || isNaN(S.cash[c])) S.cash[c] = 0;
  });
  // SEED GUARD (audit 2026-08-01). The verdict itself is computed ABOVE, before anything can
  // be written — see the block before runMigrations. Only the seeding is left here, because
  // it has to happen after the migrations and the cash normalisation.
  // Only init SEED if truly empty (first run, no imported data) AND that emptiness is proven
  if (!S.holdings.length && _provablyEmpty) {
    S.holdings = SEED.map(s => ({
      id: uid(),
      ticker: s.tk,
      name: s.nm,
      type: s.tp,
      subType: s.st,
      portfolioName: s.pn,
      broker: s.bk,
      buyWindow: s.bw,
      allocationPct: s.al,
      currency: s.cr,
      qty: 0,
      avgCost: 0
    }));
    S.monthlyRates = { ...SEED_R };
    S.cash = { USD: 0, TRY: 0, EUR: 0 };
    S.cashTxs = [];
    lg('system', '—', 'Init: 29 holdings, 3 months');
    save();
  }

  // A load that reached here without failing IS evidence, so retire the marker — including one
  // inherited from the blob we just read. Left standing it would make every future boot refuse
  // to seed a genuinely new account.
  if (!_loadFailed && S._provisional) {
    delete S._provisional;
    console.warn('[ZAGX] provisional marker cleared — this load established what the account holds');
  }

  // Seed scalar baseline from the loaded state so the first save only stamps genuine user changes (D-14)
  seedScalarBaseline();

  // Start background cloud polling for multi-device sync
  if (_sbReady && _sbUser && _isOnline) startCloudPoll();

  // Call on-load callback if set
  if (_onLoadComplete) _onLoadComplete();
}

// --- PERIODIC CLOUD POLLING (multi-device stale state detection) ---

const POLL_INTERVAL_MS = 300000; // 5 minutes (reduced from 60s to ease Supabase CPU)

/**
 * Start background polling for cloud changes from other devices.
 * Compares cloud _lastModified with local to detect stale state.
 * SYNC-02 Fix: Detect changes from other devices while app is running.
 */
export function startCloudPoll() {
  stopCloudPoll(); // prevent duplicates
  if (!_sbReady || !_sbUser) return;

  _pollTimer = setInterval(async () => {
    if (!_isOnline || !_sbReady || !_sbUser) return;
    if (!sbBreaker.canRequest()) return; // fail-fast when pool exhausted

    const ac = new AbortController();
    const timeout = setTimeout(() => ac.abort(), 10000); // PERF-02: 10s abort timeout
    try {
      const headers = await _sbHeaders();
      if (!headers) { clearTimeout(timeout); return; }
      headers['Prefer'] = 'return=representation';
      let resp = await fetch(
        SB_REST + '/user_state?user_id=eq.' + _sbUser.id + '&select=version,updated_at',
        { headers, signal: ac.signal }
      );
      // Fallback if version column doesn't exist
      if (resp.status === 400) {
        resp = await fetch(
          SB_REST + '/user_state?user_id=eq.' + _sbUser.id + '&select=updated_at',
          { headers, signal: ac.signal }
        );
      }
      if (!resp.ok) {
        sbBreaker.recordFailure();
        return;
      }
      sbBreaker.recordSuccess();
      const rows = await resp.json();
      if (!Array.isArray(rows) || rows.length === 0) return;

      const remoteVersion = rows[0].version; // undefined if column missing

      // If cloud version is ahead of our known version, another device saved
      if (typeof remoteVersion === 'number' && remoteVersion > _cloudVersion) {
        console.log('[ZAGX] Cloud version changed: local v' + _cloudVersion + ' → remote v' + remoteVersion + ' — syncing...');
        updateSyncBadge('cloud', 'syncing...');

        const cloudData = await cloudLoad();
        if (!cloudData) return;

        // Merge with current local state
        const { state: merged, conflicts, hasConflicts } = mergeStates(S, cloudData);
        Object.assign(S, merged);
        // Re-seed scalar baseline after poll-merge so the next save doesn't misattribute changes (D-11)
        seedScalarBaseline();

        // Update local caches
        idbSaveState(S);
        safeLocalSave(S);

        if (hasConflicts) {
          toast(conflicts.length + ' sync conflict(s) from another device', 'a-r');
          setTimeout(() => showConflictModal(conflicts, merged), 500);
          updateSyncBadge('cloud', 'conflicts!');
        } else {
          toast('Synced changes from another device', 'a-g');
          updateSyncBadge('cloud', 'synced');
        }

        // Routine remote-update polls are NOT logged (2026-07-15: 586/1000 audit entries were
        // "Poll: vN" — own saves + multi-tab bump versions all day; toast+badge above already
        // surface the sync; conflicts keep logging via the branch above).

        // Trigger re-render if callback is set
        if (_onLoadComplete) _onLoadComplete();
      }
    } catch (e) {
      // Polling is best-effort — don't break anything on failure
      if (e.name !== 'AbortError') console.warn('[ZAGX] Poll error:', e.message);
      sbBreaker.recordFailure();
    } finally {
      clearTimeout(timeout);
    }
  }, POLL_INTERVAL_MS);
}

/**
 * Stop background polling (called on logout or when going offline)
 */
export function stopCloudPoll() {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
}

// --- HISTORY LOGGER (used by load to record init/merge events) ---

/**
 * Append event to history (auto-prunes to 1000)
 */
function lg(a, t, d) {
  S.history.unshift({ ts: now(), action: a, ticker: t, details: d });
  if (S.history.length > 1000) S.history.pop();
}
